import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Trash2, Save } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddStartup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Master data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [employeeCounts, setEmployeeCounts] = useState([]);
  
  // Filtered data for cascading dropdowns
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);

  const [formData, setFormData] = useState({
    startup_name: '',
    logo: null,
    founding_year: new Date().getFullYear(),
    industry: '',
    country_id: '',
    state_id: '',
    city: '',
    employee_count: '',
    website_url: '',
    contact_email: '',
    contact_phone: '',
    contact_country_code: '',
    founders: [{ name: '', phone: '', email: '', country_code: '', designation: '' }]
  });

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (formData.country_id) {
      setFilteredStates(states.filter(s => s.country_id === formData.country_id));
    } else {
      setFilteredStates([]);
    }
    setFormData(prev => ({ ...prev, state_id: '', city: '' }));
  }, [formData.country_id, states]);

  useEffect(() => {
    if (formData.state_id) {
      setFilteredCities(cities.filter(c => c.state_id === formData.state_id));
    } else {
      setFilteredCities([]);
    }
    setFormData(prev => ({ ...prev, city: '' }));
  }, [formData.state_id, cities]);

  const fetchMasterData = async () => {
    try {
      const token = Cookies.get('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [countriesRes, statesRes, citiesRes, industriesRes, employeeCountsRes] = await Promise.all([
        axios.get(`${API}/master/countries`, { headers }),
        axios.get(`${API}/master/states`, { headers }),
        axios.get(`${API}/master/cities`, { headers }),
        axios.get(`${API}/master/industries`, { headers }),
        axios.get(`${API}/master/employee-counts`, { headers })
      ]);

      setCountries(countriesRes.data.data || []);
      setStates(statesRes.data.data || []);
      setCities(citiesRes.data.data || []);
      setIndustries(industriesRes.data.data || []);
      setEmployeeCounts(employeeCountsRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load master data');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For now, we'll convert to base64 or you can upload to a server
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFounderChange = (index, field, value) => {
    const newFounders = [...formData.founders];
    newFounders[index][field] = value;
    setFormData(prev => ({ ...prev, founders: newFounders }));
  };

  const addFounder = () => {
    setFormData(prev => ({
      ...prev,
      founders: [...prev.founders, { name: '', phone: '', email: '', country_code: '', designation: '' }]
    }));
  };

  const removeFounder = (index) => {
    if (formData.founders.length > 1) {
      const newFounders = formData.founders.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, founders: newFounders }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = Cookies.get('admin_token');
      const user = JSON.parse(Cookies.get('admin_user') || '{}');

      // Get the selected city name from the cities array
      const selectedCity = cities.find(c => c.id === formData.city);

      const submitData = {
        startup_name: formData.startup_name,
        logo: formData.logo || undefined,
        founding_year: parseInt(formData.founding_year),
        industry: formData.industry,
        city: selectedCity ? selectedCity.name : formData.city,
        employee_count: formData.employee_count,
        website_url: formData.website_url || undefined,
        contact_email: formData.contact_email || undefined,
        contact_phone: formData.contact_phone || undefined,
        contact_country_code: formData.contact_country_code || undefined,
        founders: formData.founders,
        added_by: user.email || 'Admin'
      };

      const response = await axios.post(`${API}/startups/`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Startup added successfully!');
      setTimeout(() => {
        navigate('/admin/startups');
      }, 1500);
    } catch (err) {
      console.error('Error adding startup:', err.response?.data);
      
      // Handle validation errors (array of error objects)
      if (err.response?.data?.detail && Array.isArray(err.response.data.detail)) {
        const errorMessages = err.response.data.detail.map(error => {
          if (typeof error === 'object' && error.msg) {
            return `${error.loc ? error.loc.join('.') + ': ' : ''}${error.msg}`;
          }
          return String(error);
        }).join(', ');
        toast.error(errorMessages || 'Validation failed');
      } else if (err.response?.data?.detail && typeof err.response.data.detail === 'string') {
        toast.error(err.response.data.detail);
      } else {
        toast.error('Failed to add startup');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold font-heading mb-2">Add New Startup</h1>
        <p className="text-muted-foreground">Enter startup details and founder information</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Startup details and company information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startup_name">Startup Name *</Label>
                <Input
                  id="startup_name"
                  name="startup_name"
                  value={formData.startup_name}
                  onChange={handleChange}
                  required
                  placeholder="Enter startup name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo">Logo (Upload Image)</Label>
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                {formData.logo && (
                  <img src={formData.logo} alt="Logo preview" className="h-16 w-16 object-contain mt-2 border rounded" />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="founding_year">Founding Year *</Label>
                <Input
                  id="founding_year"
                  name="founding_year"
                  type="number"
                  min="1900"
                  max={new Date().getFullYear()}
                  value={formData.founding_year}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry *</Label>
                <Select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select industry</option>
                  {industries.map(ind => (
                    <option key={ind.id} value={ind.name}>{ind.name}</option>
                  ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country_id">Country *</Label>
                <Select
                  id="country_id"
                  name="country_id"
                  value={formData.country_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select country</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>{country.name}</option>
                  ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="state_id">State *</Label>
                <Select
                  id="state_id"
                  name="state_id"
                  value={formData.state_id}
                  onChange={handleChange}
                  required
                  disabled={!formData.country_id}
                >
                  <option value="">Select state</option>
                  {filteredStates.map(state => (
                    <option key={state.id} value={state.id}>{state.name}</option>
                  ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City / Headquarters *</Label>
                <Select
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={!formData.state_id}
                >
                  <option value="">Select city</option>
                  {filteredCities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="employee_count">Employee Count *</Label>
                <Select
                  id="employee_count"
                  name="employee_count"
                  value={formData.employee_count}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select employee count</option>
                  {employeeCounts.map(count => (
                    <option key={count.id} value={count.range}>{count.range}</option>
                  ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website_url">Website URL</Label>
                <Input
                  id="website_url"
                  name="website_url"
                  type="url"
                  value={formData.website_url}
                  onChange={handleChange}
                  placeholder="https://startup.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={handleChange}
                  placeholder="contact@startup.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <div className="flex gap-2">
                  <Select
                    name="contact_country_code"
                    value={formData.contact_country_code}
                    onChange={handleChange}
                    className="w-[180px]"
                  >
                    <option value="">Select code</option>
                    {countries.map(country => (
                      <option key={country.id} value={country.country_code}>
                        {country.name} ({country.country_code})
                      </option>
                    ))}
                  </Select>
                  <Input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="9876543210"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Founder Details */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Founder Details</CardTitle>
                <CardDescription>Add founder information (at least one required)</CardDescription>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addFounder}>
                <Plus className="h-4 w-4 mr-2" />
                Add Founder
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {formData.founders.map((founder, index) => (
              <div key={index} className="border border-border rounded-lg p-4 relative">
                {formData.founders.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => removeFounder(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
                <h4 className="font-medium mb-4">Founder {index + 1}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={founder.name}
                      onChange={(e) => handleFounderChange(index, 'name', e.target.value)}
                      required
                      placeholder="Founder name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current Designation</Label>
                    <Input
                      value={founder.designation}
                      onChange={(e) => handleFounderChange(index, 'designation', e.target.value)}
                      placeholder="CEO, CTO, etc. (optional)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={founder.email}
                      onChange={(e) => handleFounderChange(index, 'email', e.target.value)}
                      required
                      placeholder="founder@email.com"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Phone Number *</Label>
                    <div className="flex gap-2">
                      <Select
                        value={founder.country_code}
                        onChange={(e) => handleFounderChange(index, 'country_code', e.target.value)}
                        required
                        className="w-[180px]"
                      >
                        <option value="">Select code</option>
                        {countries.map(country => (
                          <option key={country.id} value={country.country_code}>
                            {country.name} ({country.country_code})
                          </option>
                        ))}
                      </Select>
                      <Input
                        type="tel"
                        value={founder.phone}
                        onChange={(e) => handleFounderChange(index, 'phone', e.target.value)}
                        required
                        placeholder="9876543210"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex gap-4">
          <Button
            type="submit"
            variant="editorial"
            size="lg"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Startup
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => navigate('/admin/startups')}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
